import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { Lot } from "../../models/lot.model";
import { AdresseFacadeService } from "../../../adresse/services/component-store/adresse-facade.service";
import { Adresse } from "../../../adresse/models/adresse.model";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ExpandButtonComponent } from "../../../../shared/components/expand-button/expand-button.component";
import { LotPrepaSummaryComponent } from "../lot-prepa-summary/lot-prepa-summary.component";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { ArticleInfosHelperService } from "../../../articles/services/article-infos/article-infos-helper.service";
import { DechargementApiService } from "../../../commande/services/dechargement-store/dechargement-api.service";
import { CostFacadeService } from "../../../cost/services/store/cost-facade.service";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import {
  LotReservationsSummary,
  PrepreparationService,
} from "../../../prepreparation/services/prepreparation.service";

interface AdresseRecap {
  adresseId: string;
  label: string;
  total: number;
  stock: number;
  piedDeChaine: number;
  zoneExpedition: number;
  zoneNonConforme: number;
  zoneQuarantaine: number;
}

@Component({
  selector: "vl-lot-stock-summary",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ChipsComponent,
    ExpandButtonComponent,
    AutoSignificantDigitsPipe,
    LotPrepaSummaryComponent,
  ],
  templateUrl: "./lot-stock-summary.component.html",
  styleUrls: ["./lot-stock-summary.component.scss"],
})
export class LotStockSummaryComponent {
  private router = inject(Router);
  adresseFacadeService = inject(AdresseFacadeService);
  articleInfosFacade = inject(ArticlesInfosFacadeService);
  private dechargementApi = inject(DechargementApiService);
  private costFacade = inject(CostFacadeService);
  private loadedCostLotIds = new Set<string>();
  private prepService = inject(PrepreparationService);
  private loadedReservationsLotIds = new Set<string>();
  /** Réservations pré-prépa par lotId. */
  reservationsByLot = signal<Map<string, LotReservationsSummary>>(new Map());
  /** Résumé "en cours de déchargement" pour l'articleId courant. */
  infoDechargementSig = signal<{ quantite: number; nbDechargements: number }>({
    quantite: 0,
    nbDechargements: 0,
  });
  private loadedArticleIdForInfo: string | null = null;

  adresses = computed(() => {
    return this.adresseFacadeService
      .getAllEntities()()
      .filter(
        (a) =>
          !a.tiersId &&
          !a.isZoneQuarantaine &&
          !a.isZoneNonConforme &&
          !a.isZoneExpedition &&
          !a.isZoneReception &&
          !a.isPiedDeChaine &&
          !a.isZoneTransfert,
      );
  });

  articleId = input<string>();
  highlightAdresseId = input<string>();
  showOtherAdresses = signal(false);

  articleInfo = computed(() => {
    const id = this.articleId();
    return id ? this.articleInfosFacade.getByArticleId(id)() : undefined;
  });

  unite = computed(() => {
    if (this.articleInfo()) {
      return ArticleInfosHelperService.uniteDenomination(this.articleInfo());
    }

    return "u";
  });

  constructor() {
    this.adresseFacadeService.loadAllForGroup();

    // Charger le résumé "en cours de déchargement" via un endpoint agrégé
    // côté back — bien plus rapide que de récupérer tous les déchargements
    // (cf. /api/dechargements/in-progress-summary/article/:id).
    effect(
      () => {
        const articleId = this.articleId();
        if (!articleId || this.loadedArticleIdForInfo === articleId) return;
        this.loadedArticleIdForInfo = articleId;
        firstValueFrom(this.dechargementApi.inProgressSummaryForArticle(articleId))
          // Garde-fou : l'endpoint peut renvoyer null (ex: réponse vide, ou
          // mock du mode tutoriel). On ne doit JAMAIS poser null dans le
          // signal, sinon le template (infoDechargement().quantite) lève à
          // chaque cycle de détection → boucle d'erreur qui fige l'écran.
          .then((r) => this.infoDechargementSig.set(r ?? { quantite: 0, nbDechargements: 0 }))
          .catch(() => {
            // silencieux : l'info "en cours" n'est pas critique
          });
      },
      { allowSignalWrites: true },
    );

    // Charger les coûts pour les lots sans lastCost
    effect(() => {
      const lots = this.lots() ?? [];
      for (const lot of lots) {
        if (!lot.lastCost && lot.id && !this.loadedCostLotIds.has(lot.id)) {
          this.loadedCostLotIds.add(lot.id);
          this.costFacade.loadForLot(lot.id);
        }
      }
    });

    // Charger les réservations pré-prépa pour chaque lot
    effect(
      () => {
        const lots = this.lots() ?? [];
        for (const lot of lots) {
          if (lot.id && !this.loadedReservationsLotIds.has(lot.id)) {
            this.loadedReservationsLotIds.add(lot.id);
            this.prepService
              .loadReservationsSummaryPourLot(lot.id)
              .then((summary) => {
                const map = new Map(this.reservationsByLot());
                map.set(lot.id, summary);
                this.reservationsByLot.set(map);
              })
              .catch(() => {
                // ignore : si l'endpoint échoue (e.g. pas de prépa), on
                // n'affiche rien — pas critique pour le récap
              });
          }
        }
      },
      { allowSignalWrites: true },
    );
  }

  /** Σ qty VALIDEE non consommée pour tous les lots affichés. */
  reserveValideeTotal = computed(() => {
    const map = this.reservationsByLot();
    let total = 0;
    for (const lot of this.lots() ?? []) {
      total += map.get(lot.id)?.reserveValidee ?? 0;
    }
    return total;
  });

  /** Σ qty DRAFT non consommée pour tous les lots affichés. */
  reserveDraftTotal = computed(() => {
    const map = this.reservationsByLot();
    let total = 0;
    for (const lot of this.lots() ?? []) {
      total += map.get(lot.id)?.reserveDraft ?? 0;
    }
    return total;
  });

  /** Détail aplati des lignes commande qui réservent ces lots. */
  reservationsLignes = computed(() => {
    const map = this.reservationsByLot();
    const all: { lotId: string; lotNumero: string | null; row: any }[] = [];
    for (const lot of this.lots() ?? []) {
      const summary = map.get(lot.id);
      if (!summary) continue;
      for (const r of summary.lignes) {
        all.push({ lotId: lot.id, lotNumero: lot.numero ?? null, row: r });
      }
    }
    return all;
  });

  /** Total dispo (stock - reserveValidee). */
  dispoTotal = computed(() => {
    return Math.max(0, this.displayTotal() - this.reserveValideeTotal());
  });

  showReservationsDetail = signal(false);

  // Input : liste de lots
  lots = input<Lot[]>([]);

  // Quantité en cours de déchargement (non encore réceptionnée). Lue depuis
  // l'endpoint agrégé back (cf. effect dans le constructor).
  infoDechargement = computed(() => this.infoDechargementSig());

  recap = computed(() => {
    const lots = this.lots() ?? [];
    const adressesPrincipales = this.adresses() ?? [];
    const allStocks = lots
      .filter((l) => l.articleId === this.articleId())
      .flatMap((l) => l.stocks ?? []);

    // Index des adresses principales par id
    const mainById = new Map<string, Adresse>();
    for (const a of adressesPrincipales) {
      mainById.set(a.id, a);
    }

    const recapByMainId = new Map<string, AdresseRecap>();
    let totalGlobal = 0;

    for (const stock of allStocks) {
      if (!stock || !stock.adresse) continue;

      const qty = stock.quantite ?? 0;
      if (!qty) continue;

      const addr = stock.adresse;

      // Si l'adresse est une zone spéciale, regrouper sous l'adresse parente
      // Sinon (adresse principale), regrouper sous elle-même
      const isZone =
        addr.isPiedDeChaine ||
        addr.isZoneExpedition ||
        addr.isZoneNonConforme ||
        addr.isZoneQuarantaine ||
        addr.isZoneReception ||
        addr.isZoneTransfert;
      const mainId = isZone
        ? addr.adresseRefId ?? addr.id
        : addr.id;
      const mainAddr = mainById.get(mainId);

      // On récupère ou crée le bloc récap pour cette adresse principale
      let recap = recapByMainId.get(mainId);
      if (!recap) {
        recap = {
          adresseId: mainId,
          label: mainAddr?.denomination ?? addr.denomination ?? "Sans adresse",
          total: 0,
          stock: 0,
          piedDeChaine: 0,
          zoneExpedition: 0,
          zoneNonConforme: 0,
          zoneQuarantaine: 0,
        };
        recapByMainId.set(mainId, recap);
      }

      recap.total += qty;
      totalGlobal += qty;

      // Répartition par type de zone dans l’entrepôt
      if (addr.isPiedDeChaine) {
        recap.piedDeChaine += qty;
      } else if (addr.isZoneExpedition) {
        recap.zoneExpedition += qty;
      } else if (addr.isZoneNonConforme) {
        recap.zoneNonConforme += qty;
      } else if (addr.isZoneQuarantaine) {
        recap.zoneQuarantaine += qty;
      } else {
        // Stock "normal" dans l'adresse
        recap.stock += qty;
      }
    }

    const adresses = Array.from(recapByMainId.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );

    return {
      total: totalGlobal,
      adresses,
    };
  });

  /**
   * LOT C — Ventilation par PROPRIÉTAIRE. Affichée uniquement s'il existe de la
   * consignation (stock appartenant à un client, pas à l'entreprise) — ex.
   * vente « facturé — gardé en stock ». Sinon (tout à l'entreprise), masquée.
   */
  ownersBreakdown = computed(() => {
    const allStocks = (this.lots() ?? [])
      .filter((l) => l.articleId === this.articleId())
      .flatMap((l) => l.stocks ?? []);
    const map = new Map<
      string,
      { label: string; qty: number; isEntreprise: boolean }
    >();
    for (const s of allStocks) {
      const qty = s.quantite ?? 0;
      if (!qty) continue;
      const key = s.tiersId ?? "__entreprise__";
      let g = map.get(key);
      if (!g) {
        g = {
          label: s.tiersId
            ? (s.tiers?.denomination ?? "Client (consignation)")
            : "Entreprise",
          qty: 0,
          isEntreprise: !s.tiersId,
        };
        map.set(key, g);
      }
      g.qty += qty;
    }
    const rows = [...map.values()];
    // Seulement si ≥1 propriétaire ≠ entreprise (sinon rien d'intéressant).
    if (!rows.some((r) => !r.isEntreprise)) return [];
    return rows.sort((a, b) => Number(b.isEntreprise) - Number(a.isEntreprise));
  });

  valeurTotaleStock = computed(() => {
    const lots = (this.lots() ?? []).filter((l) => l.articleId === this.articleId());
    let total = 0;
    for (const lot of lots) {
      const cost = lot.lastCost ?? this.costFacade.getLastCostForLot(lot.id)();
      if (!cost) continue;
      const valeurUnitaire = (cost.coutMaterielUnitaire ?? 0) + (cost.coutPersonnelUnitaire ?? 0);
      const quantite = (lot.stocks ?? []).reduce((sum, s) => sum + (s.quantite ?? 0), 0);
      total += valeurUnitaire * quantite;
    }
    return total;
  });

  valeurUnitairePonderee = computed(() => {
    const totalQte = this.recap()?.total ?? 0;
    if (!totalQte) return null;
    return this.valeurTotaleStock() / totalQte;
  });

  primaryRecaps = computed(() => {
    const id = this.highlightAdresseId();
    if (!id) return null;
    return this.recap()?.adresses.filter((a) => a.adresseId === id) ?? [];
  });

  otherRecaps = computed(() => {
    const id = this.highlightAdresseId();
    if (!id) return null;
    return this.recap()?.adresses.filter((a) => a.adresseId !== id) ?? [];
  });

  displayTotal = computed(() => {
    const primary = this.primaryRecaps();
    if (primary) {
      return primary.reduce((sum, a) => sum + a.total, 0);
    }
    return this.recap()?.total ?? 0;
  });

  goToInventory() {
    this.router.navigate(["/articles", "inventory"], {
      queryParams: { articleId: this.articleId() },
    });
  }

  /** Navigue vers la fiche commande client (onglet préparation). */
  goToCommande(commandeClientId: string, event?: Event) {
    event?.stopPropagation();
    this.router.navigateByUrl(
      `/commandeclient/details/id/${commandeClientId}/preparation`,
    );
  }

  iconFor(k: string) {
    switch (k) {
      case "nonConforme":
        return "error_outline";
      case "quarantaine":
        return "warning";
      case "reception":
        return "move_to_inbox";
      case "expedition":
        return "outbox";
      case "pdc":
        return "precision_manufacturing";
      case "stock":
        return "package_2";
      default:
        return "info";
    }
  }
}
